const areEqual = (left, right) => {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch (error) {
    return left === right;
  }
};

const getActivities = (document = {}) => {
  const section = document
    && document.sections
    && document.sections.job_steps_hazards_consequences_controls;
  if (!section || !section.data) return [];
  return Array.isArray(section.data)
    ? section.data
    : (Array.isArray(section.data.activities) ? section.data.activities : []);
};

const getId = (value, keys) => {
  for (let index = 0; index < keys.length; index += 1) {
    if (value && value[keys[index]] !== undefined && value[keys[index]] !== null) {
      return value[keys[index]];
    }
  }
  return null;
};

const getSelectedRiskValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "object") {
    return {
      id: value.id,
      name: value.name,
      value: value.value
    };
  }

  return value;
};

const addChange = (changes, {
  itemId = undefined,
  itemLabel = undefined,
  fieldName,
  fieldLabel,
  changeType = "inline",
  action = "updated",
  previousValue,
  newValue
}) => {
  if (itemId !== undefined) {
    changes.push({
      item_id: itemId,
      item_label: itemLabel || null,
      field_name: fieldName,
      field_label: fieldLabel,
      change_type: changeType,
      action,
      previous_value: previousValue,
      new_value: newValue
    });
    return;
  }

  changes.push({
    field_name: fieldName,
    field_label: fieldLabel,
    change_type: changeType,
    action,
    previous_value: previousValue,
    new_value: newValue
  });
};

const compareField = (changes, previous, next, fieldName, fieldLabel, options = {}) => {
  if (next === undefined && previous !== undefined) {
    return;
  }
  const previousValue = options.normalize ? options.normalize(previous) : previous;
  const newValue = options.normalize ? options.normalize(next) : next;
  if (areEqual(previousValue, newValue)) {
    return;
  }

  addChange(changes, {
    ...options,
    fieldName,
    fieldLabel,
    previousValue,
    newValue,
    action: options.action || "updated"
  });
};

const compareNestedCollection = (changes, previousItems, nextItems, config) => {
  const previousById = new Map(previousItems.map((item) => [getId(item, config.idKeys), item]));
  const nextById = new Map(nextItems.map((item) => [getId(item, config.idKeys), item]));
  const ids = new Set([...previousById.keys(), ...nextById.keys()]);

  ids.forEach((id) => {
    const previous = previousById.get(id);
    const next = nextById.get(id);
    if (!previous || !next) {
      addChange(changes, {
        fieldName: config.collectionFieldName,
        fieldLabel: config.collectionLabel,
        changeType: "multiple_items",
        action: next ? "added" : "deleted",
        previousValue: next ? [] : [config.toValue(previous)],
        newValue: next ? [config.toValue(next)] : []
      });
      return;
    }

    config.fields.forEach((field) => {
      compareField(
        changes,
        previous[field.key],
        next[field.key],
        field.name,
        field.label,
        {
          itemId: id,
          itemLabel: next[config.labelKey] || previous[config.labelKey],
          changeType: field.changeType || "inline",
          normalize: field.normalize
        }
      );
    });
  });
};

const compareActivity = (previous, next) => {
  const changes = [];
  const activityId = getId(next || previous, ["activity_id"]);
  const activityLabel = (next || previous).activity_name || null;

  if (!previous || !next) {
    addChange(changes, {
      fieldName: "activity",
      fieldLabel: "Activity",
      changeType: "summary",
      action: next ? "added" : "deleted",
      previousValue: previous || null,
      newValue: next || null
    });
    return { activityId, activityLabel, changes };
  }

  const previousActive = previous.toggled_active;
  const nextActive = next.toggled_active;
  compareField(changes, previous.activity_name, next.activity_name, "activity_name", "Activity Name");
  compareField(
    changes,
    Number(previousActive),
    Number(nextActive),
    "toggled_active",
    "Activity Status",
    {
      action: Number(next.active) === 1 ? "toggled_on" : "toggled_off"
    }
  );

  compareNestedCollection(changes, previous.fm_swms_actions_v2 || [], next.fm_swms_actions_v2 || [], {
    idKeys: ["action_id"],
    labelKey: "action_name",
    collectionFieldName: "actions",
    collectionLabel: "Actions",
    toValue: (action) => ({ id: action.action_id, action_name: action.action_name }),
    fields: [
      { key: "action_name", name: "action_name", label: "Action Name" },
      { key: "action_description", name: "action_description", label: "Action Description" },
      { key: "control_measures", name: "control_measures", label: "Control Measures" },
      { key: "persons_in_control", name: "persons_in_control", label: "Persons in Control" },
      {
        key: "before_likelihood",
        name: "before_likelihood",
        label: "Before Controls Likelihood",
        normalize: getSelectedRiskValue
      },
      {
        key: "after_likelihood",
        name: "after_likelihood",
        label: "After Controls Likelihood",
        normalize: getSelectedRiskValue
      },
      {
        key: "before_consequence",
        name: "before_consequence",
        label: "Before Controls Consequence",
        normalize: getSelectedRiskValue
      },
      {
        key: "after_consequence",
        name: "after_consequence",
        label: "After Controls Consequence",
        normalize: getSelectedRiskValue
      }
    ]
  });

  const previousActions = new Map((previous.fm_swms_actions_v2 || []).map((action) => [action.action_id, action]));
  const nextActions = new Map((next.fm_swms_actions_v2 || []).map((action) => [action.action_id, action]));
  [...previousActions.keys()].forEach((actionId) => {
    if (!nextActions.has(actionId)) return;
    const previousAction = previousActions.get(actionId);
    const nextAction = nextActions.get(actionId);
    compareNestedCollection(changes, previousAction.fm_swms_activities_hazards_v2 || [], nextAction.fm_swms_activities_hazards_v2 || [], {
      idKeys: ["hazard_id"],
      labelKey: "hazard_name",
      collectionFieldName: "hazards",
      collectionLabel: "Hazards",
      toValue: (hazard) => ({ id: hazard.hazard_id, hazard_name: hazard.hazard_name }),
      fields: [
        { key: "hazard_name", name: "hazard_name", label: "Hazard Name" }
      ]
    });

    const previousHazards = new Map((previousAction.fm_swms_activities_hazards_v2 || []).map((hazard) => [hazard.hazard_id, hazard]));
    const nextHazards = new Map((nextAction.fm_swms_activities_hazards_v2 || []).map((hazard) => [hazard.hazard_id, hazard]));
    [...previousHazards.keys()].forEach((hazardId) => {
      if (!nextHazards.has(hazardId)) return;
      compareNestedCollection(
        changes,
        previousHazards.get(hazardId).fm_swms_hazards_consequences_v2 || [],
        nextHazards.get(hazardId).fm_swms_hazards_consequences_v2 || [],
        {
          idKeys: ["consequence_id"],
          labelKey: "consequences_name",
          collectionFieldName: "consequences",
          collectionLabel: "Consequences",
          toValue: (consequence) => ({
            id: consequence.consequence_id,
            consequences_name: consequence.consequences_name
          }),
          fields: [
            { key: "consequences_name", name: "consequences_name", label: "Consequence Name" },
            { key: "control_measures", name: "control_measures", label: "Control Measures" },
            { key: "initial_risk", name: "initial_risk", label: "Initial Risk" },
            { key: "residual_risk", name: "residual_risk", label: "Residual Risk" },
            { key: "action_by", name: "action_by", label: "Action By" }
          ]
        }
      );
    });
  });

  return { activityId, activityLabel, changes };
};

export const buildJobStepsChanges = (initialDocument = {}, submittedDocument = {}) => {
  const initialSection = initialDocument.sections
    && initialDocument.sections.job_steps_hazards_consequences_controls;
  const submittedSection = submittedDocument.sections
    && submittedDocument.sections.job_steps_hazards_consequences_controls;
  const initialData = initialSection && initialSection.data ? initialSection.data : {};
  const submittedData = submittedSection && submittedSection.data ? submittedSection.data : {};
  const initialActivities = getActivities(initialDocument);
  const submittedActivities = getActivities(submittedDocument);
  const initialById = new Map(initialActivities.map((activity) => [activity.activity_id, activity]));
  const submittedById = new Map(submittedActivities.map((activity) => [activity.activity_id, activity]));
  const activityIds = new Set([...initialById.keys(), ...submittedById.keys()]);
  const items = [];
  const sectionLabel = submittedSection && submittedSection.display_name;

  const initialToggle = initialData.toggle !== undefined
    ? initialData.toggle
    : initialData.toggled_active;
  const submittedToggle = submittedData.toggle !== undefined
    ? submittedData.toggle
    : submittedData.toggled_active;
  if (!areEqual(Number(initialToggle), Number(submittedToggle))) {
    items.push({
      item_id: null,
      item_label: null,
      section_changes: [{
        section_key: "job_steps_hazards_consequences_controls",
        section_label: sectionLabel,
        changes: [{
          field_name: "toggle",
          field_label: "Job Steps, Hazards, Consequences & Controls Toggle",
          change_type: "inline",
          action: Number(submittedToggle) === 1 ? "toggled_on" : "toggled_off",
          previous_value: Number(initialToggle),
          new_value: Number(submittedToggle)
        }]
      }]
    });
  }

  activityIds.forEach((activityId) => {
    const compared = compareActivity(initialById.get(activityId), submittedById.get(activityId));
    if (compared.changes.length === 0) {
      return;
    }

    items.push({
      item_id: compared.activityId,
      item_label: compared.activityLabel,
      section_changes: [{
        section_key: "job_steps_hazards_consequences_controls",
        section_label: sectionLabel,
        changes: compared.changes
      }]
    });
  });

  const initialOrder = initialActivities.map((activity) => activity.activity_id);
  const submittedOrder = submittedActivities.map((activity) => activity.activity_id);
  const sameActivitySet = initialOrder.length === submittedOrder.length
    && initialOrder.every((activityId) => submittedOrder.includes(activityId));
  if (sameActivitySet && !areEqual(initialOrder, submittedOrder)) {
    const initialPositions = new Map(initialOrder.map((activityId, index) => [activityId, index + 1]));
    const movedActivityIds = new Set(
      submittedOrder.filter((activityId, index) => initialPositions.get(activityId) !== index + 1)
    );
    items.push({
      item_id: null,
      item_label: null,
      section_changes: [{
        section_key: "job_steps_hazards_consequences_controls",
        section_label: sectionLabel,
        changes: [{
          field_name: "activity_order",
          field_label: "Activity Order",
          change_type: "multiple_items",
          action: "reordered",
          previous_value: initialActivities
            .map((activity, index) => ({
            id: activity.activity_id,
            activity_name: activity.activity_name,
            position: index + 1
            }))
            .filter((activity) => movedActivityIds.has(activity.id)),
          new_value: submittedActivities
            .map((activity, index) => ({
            id: activity.activity_id,
            activity_name: activity.activity_name,
            position: index + 1
            }))
            .filter((activity) => movedActivityIds.has(activity.id))
        }]
      }]
    });
  }

  return items;
};
